import classNames from 'classnames';

// The typography model behind RCText: the source-of-truth size/weight maps, the
// canonical `{variant}-{size}[-{weight}]` union derived from them, and the pure
// functions that map a variant to its `text-*` utility classes. No React here — the
// RCText components, the drift-guard test, and Storybook all import from this module.
//
// Note: Size / weight options and defaults need to stay in sync with `mixins/rc-text.scss`.

export const propOptions = {
  default: {
    htmlTags: ['div', 'span', 'p'] as const,
    sizes: ['350', '400'] as const,
    weights: ['regular', 'bold'] as const,
  },
  display: {
    htmlTags: ['div', 'h1'] as const,
    sizes: ['1600'] as const,
    weights: ['regular', 'bold'] as const,
  },
  eyebrow: {
    htmlTags: ['div', 'p', 'span'] as const,
    sizes: ['300'] as const,
    weights: ['regular', 'bold'] as const,
  },
  fine: {
    htmlTags: ['div', 'p', 'span'] as const,
    sizes: ['300'] as const,
    weights: ['regular', 'bold'] as const,
  },
  title: {
    htmlTags: ['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const,
    sizes: ['500', '600', '700', '800', '900', '1000', '1200'] as const,
    weights: ['regular', 'bold'] as const,
  },
  utility: {
    htmlTags: ['div', 'p', 'span'] as const,
    sizes: ['350', '400', '450', '500', '550', '600'] as const,
    weights: ['regular', 'medium', 'semibold', 'bold'] as const,
  },
} as const;

export const propDefaults = {
  default: { size: '400', weight: 'regular' },
  display: { size: '1600', weight: 'bold' },
  eyebrow: { size: '300', weight: 'bold' },
  fine: { size: '300', weight: 'regular' },
  title: { size: '900', weight: 'bold' },
  utility: { size: '400', weight: 'regular' },
} as const;

export type TextVariant = keyof typeof propOptions;

// Themed-surface text role — maps 1:1 to a `.themed-text-{value}` class.
export type TextTheme = 'emphasis';

// The default weight needs no modifier class (the base `text-{variant}-{size}`
// already carries it), so excluding it from the canonical union keeps redundant
// tokens like `title-900-bold` unrepresentable and the union tight (~48, not ~66).
type NonDefaultWeight<V extends TextVariant> = Exclude<
  typeof propOptions[V]['weights'][number],
  typeof propDefaults[V]['weight']
>;

/**
 * Every valid `{variant}-{size}[-{weight}]` typography token, derived from
 * `propOptions` / `propDefaults` so the type can't drift from the emitted `text-*`
 * utility classes. Weight is folded into the token (rather than an independent prop)
 * because weight validity is variant-coupled — `utility-400-semibold` is valid,
 * `title-900-semibold` is not — so folding makes invalid combos unrepresentable.
 */
export type CanonicalTextStyle = {
  [V in TextVariant]:
  | `${V}-${typeof propOptions[V]['sizes'][number]}`
  | `${V}-${typeof propOptions[V]['sizes'][number]}-${NonDefaultWeight<V>}`;
}[TextVariant];

/**
 * Runtime mirror of `CanonicalTextStyle` (a type has no runtime presence). Generated
 * from the same maps, so it stays in lock-step with the union; the drift-guard test
 * asserts every entry maps to a class that exists in the built stylesheet.
 */
export const CANONICAL_TEXT_STYLES: CanonicalTextStyle[] = (Object.keys(propOptions) as TextVariant[])
  .flatMap((variant) =>
    propOptions[variant].sizes.flatMap((size) => {
      const base = `${variant}-${size}` as CanonicalTextStyle;
      const weighted = propOptions[variant].weights
        .filter((weight) => weight !== propDefaults[variant].weight)
        .map((weight) => `${variant}-${size}-${weight}` as CanonicalTextStyle);
      return [base, ...weighted];
    }),
  );

// A canonical token → its utility class(es) at one breakpoint (`prefix` is `''` for
// the desktop base and `tablet-` / `mobile-` otherwise). A `-{weight}` suffix (present
// only for non-default weights) adds the variant-scoped `text-{variant}--{weight}`
// modifier — the only selector specific enough to override the base class's weight
// (a generic `weight-*` utility loses to `text-*` on specificity/source order).
export function variantToClasses(token: CanonicalTextStyle, prefix: string): string {
  const [variant, size, weight] = token.split('-');
  const base = `${prefix}text-${variant}-${size}`;
  return weight ? `${base} ${prefix}text-${variant}--${weight}` : base;
}

// One static variant/size/weight → its `text-*` class(es). The weight modifier is
// emitted only for a non-default weight (the base class already carries the default).
export function staticVariantClass(variant: TextVariant, size: string, weight: string): string {
  return classNames(`text-${variant}-${size}`, {
    [`text-${variant}--${weight}`]: weight !== propDefaults[variant].weight,
  });
}

// Combines the (already-computed) text-style classes with the variant-independent
// modifiers (truncation, themed role) and the consumer `className` passthrough.
export function modifierClasses(
  textClasses: string[],
  maxLines: number | undefined,
  theme: TextTheme | undefined,
  className: string | undefined,
): string {
  return classNames(
    textClasses,
    {
      [`text-truncate-${maxLines}`]: maxLines !== undefined,
      // Maps to the shared `.themed-text-${value}` UI class (e.g. `.themed-text-emphasis`).
      [`themed-text-${theme}`]: theme !== undefined,
    },
    className,
  );
}

// Convenience wrapper for the static (single-style) subcomponents.
export function getClassnames(
  variant: TextVariant,
  size: string,
  weight: string,
  maxLines: number | undefined,
  theme?: TextTheme,
  className?: string,
): string {
  return modifierClasses([staticVariantClass(variant, size, weight)], maxLines, theme, className);
}
